import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { AuthService } from '../../Services/auth.service';
import { RoleService } from '../../Services/role.service';
import { RegisterUserRequest, GetUserByUsernameResponse } from '../../Models/Common/RegisterUserModels';
import { UserRole } from '../../Models/Common/UserRole';
import { Global } from '../../Shared/global';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { AppContext } from '../../Shared/common';
import { AppConfigService } from '../../Services/appconfig.service';
import { ApplicationConfiguration } from '../../Models/ApplicationConfiguration';

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.css']
})
export class RegisterComponent implements OnInit, OnDestroy {
  registerForm: FormGroup;
  isLoading = false;
  isCheckingUsername = false;
  isSubmitting = false;
  errorMessage = '';
  successMessage = '';
  showPassword = false;
  userRoleList: UserRole[] = [];
  usernameNotFound = false;
  userFound = false;
  userRegistered = false;

  private destroy$ = new Subject<void>();
  private usernameCheckSubject$ = new Subject<string>();

  // Theme properties
  loginLeftBoxHtml: SafeHtml = '';
  loginLeftCssClass: string = 'hero';
  loginFooterHtml: SafeHtml = '';
  appTitle: string = 'FitSpeex';
  appDescription: string = '';
  logoUrl: string = '';

  constructor(
    private formBuilder: FormBuilder,
    private authService: AuthService,
    private roleService: RoleService,
    private router: Router,
    private appctx: AppContext,
    private appConfigService: AppConfigService,
    private sanitizer: DomSanitizer
  ) {
    this.registerForm = this.createForm();
  }

  ngOnInit(): void {
    // Load user roles
    this.loadUserRoles();

    // Setup username validation on change
    this.setupUsernameValidation();

    // Redirect if already logged in
    if (this.authService.isLoggedIn()) {
      this.router.navigate([Global.DefaultPage]);
    }

    // Load and apply theme configuration
    this.loadThemeConfiguration();

    // Subscribe to theme changes
    this.appConfigService.appConfig$
      .pipe(takeUntil(this.destroy$))
      .subscribe(appConfig => {
        this.applyThemeToComponent(appConfig.CustomSettings);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.usernameCheckSubject$.complete();
  }

  /**
   * Create reactive form with validators
   */
  private createForm(): FormGroup {
    return this.formBuilder.group({
      userName: ['', [Validators.required, Validators.minLength(3)]],
      email: [
        { value: '', disabled: true },
        [Validators.required, Validators.email]
      ],
      contact1: [
        { value: '', disabled: true },
        [Validators.required, Validators.pattern(/^[\d\-\+\s\(\)]+$/)]
      ],
      password: [
        '',
        [Validators.required, Validators.minLength(6), this.passwordValidator]
      ],
      userRoles: [[], [Validators.required, this.rolesValidator]]
    });
  }

  compareRoles(o1: any, o2: any): boolean {
    if (o1 == undefined || o2 == undefined) {
      return !(o1 == undefined || o2 == undefined);
    }
    else if (o1.RoleName == undefined && o1.RoleName == undefined && o1.RoleName == undefined && o1.RoleId == undefined) {
      return o1 == o2;
    }
    else {
      return ((o1.RoleName != undefined && o1.RoleId != undefined && o1.RoleName === o2.RoleName && o1.RoleId === o2.RoleId)
        || (o1.RoleName != undefined && o1.RoleId != undefined && o1.RoleName === o2.RoleName && o1.RoleId === o2.RoleId));
    }
  }

  /**
   * Password validator: require at least one uppercase, one digit
   */
  private passwordValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null;
    }

    const password = control.value;
    const hasUppercase = /[A-Z]/.test(password);
    const hasDigit = /\d/.test(password);

    if (!hasUppercase || !hasDigit) {
      return {
        passwordStrength: {
          requiresUppercase: !hasUppercase,
          requiresDigit: !hasDigit
        }
      };
    }

    return null;
  }

  /**
   * Roles validator: ensure at least one role is selected
   */
  private rolesValidator(control: AbstractControl): ValidationErrors | null {
    const roles = control.value as UserRole[];
    if (!roles || roles.length === 0) {
      return { rolesRequired: true };
    }
    return null;
  }

  /**
   * Setup username debounce and validation
   */
  private setupUsernameValidation(): void {
    this.usernameCheckSubject$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(username => {
        if (username && username.length >= 3) {
          this.checkUsername(username);
        } else {
          this.resetUserLookup();
        }
      });
  }

  /**
   * Check if username exists and lookup user details
   */
  private checkUsername(username: string): void {
    this.isCheckingUsername = true;
    this.errorMessage = '';
    this.usernameNotFound = false;
    this.userFound = false;
    this.userRegistered = false;

    this.authService
      .getUserByUserName(username)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response && response.UserName) {
            // User found - populate email and phone
            this.populateUserData(response);
            this.userFound = true;
            this.usernameNotFound = false;
            this.userRegistered = response.IsRegistered; // Assuming API returns this flag
            this.enableOtherFields();
          } else {
            this.resetUserLookup();
          }
          this.isCheckingUsername = false;
        },
        error: (error) => {
          this.isCheckingUsername = false;
          this.userFound = false;
          this.usernameNotFound = true;
          //this.errorMessage = 'Username not found.';
          this.resetUserLookupFields();
          this.disableOtherFields();
        }
      });
  }

  /**
   * Populate user data from lookup
   */
  private populateUserData(userData: GetUserByUsernameResponse): void {
    this.registerForm.patchValue({
      email: userData.Email,
      contact1: userData.Contact1,
      userRoles: userData.UserRoles
    });

    Global.Log(`User found: ${userData.UserName}`);
  }

  /**
   * Reset user lookup
   */
  private resetUserLookup(): void {
    this.usernameNotFound = false;
    this.userFound = false;
    this.resetUserLookupFields();
    this.disableOtherFields();
  }

  /**
   * Reset user lookup fields (email and phone)
   */
  private resetUserLookupFields(): void {
    this.registerForm.patchValue({
      email: '',
      contact1: '',
      userRoles: []
    });
  }

  /**
   * Enable password and role fields after user lookup
   */
  private enableOtherFields(): void {
    if (this.registerForm.get('userRoles')?.value.length <= 0)
      this.registerForm.get('userRoles')?.enable();
    else
      this.registerForm.get('userRoles')?.disable();

    if (this.userRegistered) {
      this.registerForm.get('password')?.disable();
    }
    else {
      this.registerForm.get('password')?.enable();
    }
  }

  /**
   * Disable password and role fields when username not found
   */
  private disableOtherFields(): void {
    this.registerForm.get('password')?.disable();
    this.registerForm.get('userRoles')?.disable();
  }

  /**
   * Load available user roles
   */
  private loadUserRoles(): void {
    this.roleService
      .getRoles()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (roles) => {
          this.userRoleList = roles.filter(r => r.IsActive);
        },
        error: (error) => {
          Global.LogError('Failed to load roles:', error);
          this.errorMessage = 'Failed to load roles.';
        }
      });
  }

  /**
   * Handle username input change
   */
  onUsernameChange(username: string): void {
    this.usernameCheckSubject$.next(username);
  }

  /**
   * Toggle password visibility
   */
  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  /**
   * Handle form submission
   */
  onSubmit(): void {
    if (this.registerForm.invalid) {
      this.markFormGroupTouched(this.registerForm);
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = '';
    this.successMessage = '';

    const formValue = this.registerForm.getRawValue();
    const registerRequest: RegisterUserRequest = {
      UserName: formValue.userName,
      Email: formValue.email,
      Contact1: formValue.contact1,
      Password: formValue.password,
      UserRoles: formValue.userRoles
    };

    this.authService
      .registerUser(registerRequest)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isSubmitting = false;
          //this.successMessage = 'User registered successfully!';
          Global.Log('User registration successful');

          // Reset form after success
          setTimeout(() => {
            //this.registerForm.reset();
            this.registerForm.get('password')?.reset();
            this.userFound = false;
            this.usernameNotFound = false;
            this.userRegistered = true;
            this.disableOtherFields();
            this.successMessage = '';
            // Navigate or stay on component based on business logic
            // this.router.navigate(['/user-management']);
          }, 2000);
        },
        error: (error) => {
          this.isSubmitting = false;

          if (Array.isArray(error.errors)) {
            this.errorMessage = error.errors.join('\n');
          } else {
            this.errorMessage = error.message || 'Registration failed. Please try again.';
          }

          Global.LogError('User registration failed:', error);
        }
      });
  }

  /**
   * Mark all form controls as touched to show validation errors
   */
  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  /**
   * Get form control getters
   */
  get userName() {
    return this.registerForm.get('userName');
  }

  get email() {
    return this.registerForm.get('email');
  }

  get contact1() {
    return this.registerForm.get('contact1');
  }

  get password() {
    return this.registerForm.get('password');
  }

  get userRoles() {
    return this.registerForm.get('userRoles');
  }

  /**
   * Check if specific validation error exists
   */
  hasError(controlName: string, errorName: string): boolean {
    const control = this.registerForm.get(controlName);
    return !!(control && control.hasError(errorName) && control.touched);
  }

  /**
   * Get error message for control
   */
  getErrorMessage(controlName: string): string {
    const control = this.registerForm.get(controlName);

    if (!control || !control.errors) {
      return '';
    }

    if (control.hasError('required')) {
      return `${this.getFieldLabel(controlName)} is required`;
    }

    if (control.hasError('minlength')) {
      const minLength = control.getError('minlength').requiredLength;
      return `${this.getFieldLabel(controlName)} must be at least ${minLength} characters`;
    }

    if (control.hasError('email')) {
      return 'Please enter a valid email address';
    }

    if (control.hasError('pattern')) {
      return 'Please enter a valid phone number';
    }

    if (control.hasError('passwordStrength')) {
      const errors = control.getError('passwordStrength');
      let message = 'Password must contain ';
      if (errors.requiresUppercase) {
        message += 'at least one uppercase letter';
      }
      if (errors.requiresDigit) {
        message += errors.requiresUppercase ? ' and one digit' : 'at least one digit';
      }
      return message;
    }

    if (control.hasError('rolesRequired')) {
      return 'At least one role must be selected';
    }

    return 'Invalid input';
  }

  /**
   * Get friendly field label for error messages
   */
  private getFieldLabel(controlName: string): string {
    const labels: { [key: string]: string } = {
      userName: 'Username',
      email: 'Email',
      contact1: 'Phone Number',
      password: 'Password',
      userRoles: 'User Roles'
    };
    return labels[controlName] || controlName;
  }

  /**
   * Load theme configuration
   */
  private loadThemeConfiguration(): void {
    this.appConfigService.loadAppConfig().then(config => {
      this.applyThemeToComponent(config);

      if (!this.loginLeftBoxHtml || this.loginLeftCssClass === '') {
        this.setDefaultTheme();
      }
    }).catch(error => {
      Global.LogError('[LoginComponent] Error loading theme:', error);
      // Use defaults if theme loading fails
      this.setDefaultTheme();
    });
  }

  /**
   * Apply theme to component
   */
  private applyThemeToComponent(config: ApplicationConfiguration): void {
    if (!config || !config?.CustomSettings) {
      Global.Log('[LoginComponent] No custom settings found in config');
      this.setDefaultTheme();
      return;
    }
    if (config.CustomSettings.loginLeftBoxHtml) {
      this.loginLeftBoxHtml = this.sanitizer.bypassSecurityTrustHtml(config.CustomSettings.loginLeftBoxHtml);
    }

    if (config.CustomSettings.loginLeftCssClass) {
      this.loginLeftCssClass = config.CustomSettings.loginLeftCssClass;
    }

    if (config.CustomSettings.loginFooterHtml) {
      this.loginFooterHtml = this.sanitizer.bypassSecurityTrustHtml(config.CustomSettings.loginFooterHtml);
    }

    if (config.CustomSettings.appTitle) {
      this.appTitle = config.CustomSettings.appTitle;
    }

    if (config.CustomSettings.appDescription) {
      this.appDescription = config.CustomSettings.appDescription;
    }

    if (config.CustomSettings.logoUrl) {
      this.logoUrl = config.CustomSettings.logoUrl;
    }

    Global.Log('[LoginComponent] Theme applied');
  }

  /**
   * Set default theme if loading fails
   */
  private setDefaultTheme(): void {
    this.appTitle = 'FitSpeex';
    this.loginLeftCssClass = 'hero';
    this.loginLeftBoxHtml = this.sanitizer.bypassSecurityTrustHtml(
      `<div class="hero-overlay"></div> <div class="logo"> <div class="logo-icon">✦</div> <span>Fitspeex</span> </div> <div class="hero-content"> <h1> Move <span>Your Way</span> </h1> <p> One platform for every kind of fitness. Gym, Yoga, Calisthenics and more. </p> </div> <!-- FITNESS MENU --> <div class="fitness-menu"> <div class="menu-item"> <div class="menu-icon material-icons-round"> <mat-icon fontSet="material-symbols-rounded">fitness_center</mat-icon> </div> <span>Gym</span> </div> <div class="menu-item"> <div class="menu-icon material-icons-round"> <mat-icon fontSet="material-symbols-rounded">self_improvement</mat-icon> </div> <span>Yoga</span> </div> <div class="menu-item"> <div class="menu-icon material-icons-round"> <mat-icon fontSet="material-symbols-rounded">sports_gymnastics</mat-icon> </div> <span>Calisthenics</span> </div> <div class="menu-item"> <div class="menu-icon material-icons-round"> <mat-icon fontSet="material-symbols-rounded">directions_run</mat-icon> </div> <span>Cardio</span> </div> <div class="menu-item"> <div class="menu-icon material-icons-round"> <mat-icon fontSet="material-symbols-rounded">more_horiz</mat-icon> </div> <span>More</span> </div> </div>`
    );
    this.loginFooterHtml = this.sanitizer.bypassSecurityTrustHtml(
      `<div class="feature-card"> <div class="feature-icon material-symbols-rounded"> <mat-icon fontSet="material-symbols-rounded">grid_view</mat-icon> </div> <h3>All-in-One Fitness</h3> <p> Access gym, yoga, calisthenics, cardio and more in one place. </p> </div> <div class="feature-card"> <div class="feature-icon material-symbols-rounded"> <mat-icon fontSet="material-symbols-rounded">person</mat-icon> </div> <h3>Personalized for You</h3> <p> Your goals, your plan. We adapt to you. </p> </div> <div class="feature-card"> <div class="feature-icon material-symbols-rounded"> <mat-icon fontSet="material-symbols-rounded">analytics</mat-icon> </div> <h3>Track & Progress</h3> <p> Monitor your progress and stay motivated every day. </p> </div> <div class="feature-card"> <div class="feature-icon material-symbols-rounded"> <mat-icon fontSet="material-symbols-rounded">verified_user</mat-icon> </div> <h3>Trusted & Secure</h3> <p> Your data is safe with us. Focus on what matters. </p> </div>`
    );
  }
}
